import { Component, OnDestroy } from '@angular/core';

import { DialogService } from 'primeng/dynamicdialog';

import { combineLatest, filter, map, Observable, of, Subject, takeUntil } from 'rxjs';

import { ApiChapterSubjectCategoryListItem, ApiReplaceSubjectCategoryRequest, ChaptersClient, SubjectCategoriesClient, SubjectCategoryType } from 'content-manager/src/app/core/open-api/content-manager-api-client';
import { ProjectStateService } from 'content-manager/src/app/projects/project/project-state.service';
import { IProjectUpdateTopicsDialogData } from 'content-manager/src/app/projects/project/topics/update-topics-dialog/project-update-topics-dialog-data';
import { ProjectUpdateTopicsDialogComponent } from 'content-manager/src/app/projects/project/topics/update-topics-dialog/project-update-topics-dialog.component';
import { Filters, TableSettings } from 'sc-common';

@Component({
    templateUrl: './project-topics.component.html',
    standalone: false
})
export class ProjectTopicsComponent implements OnDestroy {

    public readonly tableModel$: Observable<{ isReadonly: boolean; tableSettings: TableSettings<ApiChapterSubjectCategoryListItem>; hasChapters: boolean; }>;

    private readonly _refreshTable$ = new Subject<void>();

    private readonly _destroy$ = new Subject<void>();

    constructor(
        private readonly _projectStateService: ProjectStateService,
        private readonly _subjectCategoriesClient: SubjectCategoriesClient,
        private readonly _chaptersClient: ChaptersClient,
        private readonly _dialogService: DialogService) {

        this.tableModel$ = combineLatest([
            _projectStateService.model$,
            this._projectStateService.switchMap(project => this._chaptersClient.hasChapters(project.id))])
            .pipe(map(([project, hasChapters]) => ({
                isReadonly: project.isReadonly,
                hasChapters: hasChapters,
                tableSettings: this._initChapterTopicsTable()
            })));

    }

    public ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
        this._refreshTable$.complete();
    }

    public openChapterTopicsDialog(chapter: ApiChapterSubjectCategoryListItem): void {

        const dialogData: IProjectUpdateTopicsDialogData = {
            topics$: this._subjectCategoriesClient.getTopics(),
            existingTopicHeadings$: of(chapter.themas.map(t => t.headingText)),
            updateAction: subjectCategories => this._projectStateService.firstSwitchMap(project =>
                this._chaptersClient.replaceSubjectCategories(
                    project.id,
                    chapter.id,
                    new ApiReplaceSubjectCategoryRequest({ subjectCategoryType: SubjectCategoryType.Topic, subjectCategories })))
        };

        this._openTopicsDialog(dialogData);
    }

    private _openTopicsDialog(dialogData: IProjectUpdateTopicsDialogData): void {

        const dialogRef = this._dialogService.open(ProjectUpdateTopicsDialogComponent, {
            width: '20rem',
            header: $localize`Update Topics`,
            closable: true,
            data: dialogData
        });

        dialogRef.onClose
            .pipe(filter(r => r), takeUntil(this._destroy$))
            .subscribe(() => this._refreshTable$.next());
    }

    private _initChapterTopicsTable(): TableSettings<ApiChapterSubjectCategoryListItem> {
        const chapterList$ = this._projectStateService.switchMap(project => this._chaptersClient.getAll(project.id));

        return TableSettings.create(ApiChapterSubjectCategoryListItem, {
            dataSource: oDataQuery => this._projectStateService.switchMap(project =>
                this._chaptersClient.getSubjectCategoryList(project.id, SubjectCategoryType.Topic, oDataQuery)),
            columnResizeMode: 'fit',
            actionsCount: 1,
            emptyMessageText: $localize`No chapters`,
            reload$: this._refreshTable$.asObservable(),
            columns: {
                pos: {
                    width: '100px',
                    centered: true
                },
                chapter: {
                    filter: Filters.multiselect(chapterList$.pipe(map(x => x.map(k => ({ id: k.name, name: k.name })))))
                },
                themas: {
                    header: $localize`Topics`,
                    displayExpr: item => item.headingText,
                    filterExpr: item => item.headingText,
                    renderAsCommaSeparatedList: true
                }
            },
            defaultSort: {
                field: x => x.pos
            }
        });
    }
}
